/**
 * Bridge between the web app and the React Native shell (hkust-path-mobile).
 *
 * The native side injects `window.ReactNativeWebView`. Messages are exchanged as
 * JSON strings via postMessage in both directions.
 *
 * Indoor positioning is now computed natively by the CloudEngine iOS SDK
 * (scan -> upload -> position). The native shell streams a fully-resolved position
 * (floor / latitude / longitude) into the web app, which only renders the blue dot.
 *
 * Message protocol (web <-> native):
 *   native -> web:  window.PathAdvisorNative = { indoorPositioning: boolean, bridgeVersion: number }
 *                    Injected at document start, before any of this app's scripts run. See
 *                    `supportsIndoorPositioning` below for why the flag exists.
 *   web  -> native: { type: 'START_POSITIONING' }
 *   web  -> native: { type: 'STOP_POSITIONING' }
 *   native -> web:  { type: 'POSITION', position: {
 *                        latitude: number, longitude: number,
 *                        accuracy?: number,
 *                        // PathAdvisor ids. The shell maps the CloudEngine floor code
 *                        // ("AB_1F") onto these; both are absent when the server could
 *                        // not place the fix on a floor (e.g. a GPS-only answer).
 *                        building_floor_id?: string,
 *                        building_id?: string
 *                    } }
 *   native -> web:  { type: 'POSITION_STATUS', status:
 *                        // preflight lifecycle (drive spinner + error modal):
 *                        'searching'|'health_failed'|'register_failed'|'ready'
 *                        // sensor / permission status:
 *                        |'denied'|'unavailable'|'bluetooth_off' }
 *   native -> web:  { type: 'HEADING', heading: number, accuracy?: number }
 *                    Which way the phone is pointing, in degrees clockwise from true north, with
 *                    the error estimate in degrees. It comes from the device compass, NOT from
 *                    CloudEngine - the SDK scans beacons and GPS only and reports no bearing.
 *                    Sent on its own rather than folded into POSITION because positions arrive at
 *                    1 Hz while the cone has to keep up with the phone being turned; native
 *                    throttles it to ~10 Hz with a small deadband.
 */

export const MessageTypes = {
	START_POSITIONING: 'START_POSITIONING',
	STOP_POSITIONING: 'STOP_POSITIONING',
	POSITION: 'POSITION',
	POSITION_STATUS: 'POSITION_STATUS',
	HEADING: 'HEADING'
};

/** True when the web app is running inside a native WebView shell — any shell, any version. */
export function isInWebView() {
	return typeof window !== 'undefined' && !!window.ReactNativeWebView;
}

/**
 * True when the host shell can actually run indoor positioning.
 *
 * Deliberately not `isInWebView()`. `window.ReactNativeWebView` comes from
 * react-native-webview itself, not from our shell, so it is present in every build that has
 * ever wrapped PathAdvisor — including the older ones with no CloudEngine SDK, which are
 * still installed on people's phones and load this same deployed web app. Those shells
 * ignore START_POSITIONING, so nothing would ever come back: the user would get a spinner
 * that runs out DETECT_TIMEOUT_MS and an error blaming a positioning server they never
 * reached. Gate the positioning UI on this instead, which only a shell carrying the SDK
 * declares (hkust-path-mobile/App.tsx).
 */
export function supportsIndoorPositioning() {
	return (
		typeof window !== 'undefined' &&
		!!window.PathAdvisorNative &&
		window.PathAdvisorNative.indoorPositioning === true
	);
}

/** Send a message to the native shell. No-op when not running in the WebView. */
export function sendToNative(message) {
	if (!isInWebView()) return false;
	try {
		window.ReactNativeWebView.postMessage(JSON.stringify(message));
		return true;
	} catch (e) {
		return false;
	}
}

/**
 * Subscribe to messages coming from the native shell.
 * iOS delivers WebView messages on `window`, Android on `document`; we listen on both.
 * @param {(msg: object) => void} handler
 * @returns {() => void} unsubscribe
 */
export function subscribeToNative(handler) {
	const listener = (event) => {
		// Ignore messages that are not from the native bridge.
		if (typeof event.data !== 'string') return;
		let parsed;
		try {
			parsed = JSON.parse(event.data);
		} catch (e) {
			return;
		}
		if (!parsed || typeof parsed.type !== 'string') return;
		handler(parsed);
	};

	window.addEventListener('message', listener);
	document.addEventListener('message', listener);

	return () => {
		window.removeEventListener('message', listener);
		document.removeEventListener('message', listener);
	};
}

const nativeBridge = {
	MessageTypes,
	isInWebView,
	supportsIndoorPositioning,
	sendToNative,
	subscribeToNative
};

export default nativeBridge;
